/**
 * ShareholderGraph - thin wrapper around the lazily loaded card graph.
 *
 * React Flow, ELK and html-to-image live only in the lazy chunk (graph/ShareholderFlowGraph), so
 * the company page and the General tab never load them. The chunk sits behind a local error
 * boundary: after a deploy removes the old hashed chunks, a stale tab gets a ChunkLoadError, which
 * must stay inside the graph box instead of reaching the app-wide error boundary.
 *
 * @module ShareholderGraph
 */
import { Component, Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';
import GeneralLoader from '../../../loaders/GeneralLoader';
import { GRAPH_COPY } from './graph/graphConstants';

export const loadShareholderFlowGraph = () => import('./graph/ShareholderFlowGraph');

const LazyShareholderFlowGraph = lazy(loadShareholderFlowGraph);

export const RELOAD_LABEL = 'Reload';

/** The fixed-height graph box, so loader, messages and graph never change the card height. */
const CanvasBox = ({ variant, children }) => (
  <div className={`sg-flow sg-flow--${variant}`}>
    <div className="sg-canvas">{children}</div>
  </div>
);

CanvasBox.propTypes = {
  variant: PropTypes.oneOf(['mini', 'popup']).isRequired,
  children: PropTypes.node
};

const reloadPage = () => window.location.reload();

/**
 * Keeps a failed chunk load (or a render error inside the graph) in the graph box. Tables,
 * filters and the General tab keep working; the user can reload the page.
 */
export class GraphChunkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, resetKey: props.resetKey };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  // A new resetKey (the next graph: filters, collapse, figures or another company) gets a fresh
  // try, since a render error may come from the previous graph's data. A chunk that failed to
  // load fails again at once.
  static getDerivedStateFromProps(props, state) {
    return props.resetKey === state.resetKey ? null : { hasError: false, resetKey: props.resetKey };
  }

  componentDidCatch(error) {
    console.error('ERROR - ShareholderGraph - The shareholder graph could not be loaded.', error);
  }

  render() {
    const { variant = 'mini', onReload = reloadPage, children = null } = this.props;
    if (!this.state.hasError) {
      return children;
    }
    return (
      <CanvasBox variant={variant}>
        <div className="sg-canvas-message sg-chunk-error" role="alert">
          <span>{GRAPH_COPY.CHUNK_ERROR}</span>
          <IconButton
            accessible
            ariaLabel={RELOAD_LABEL}
            icon={ICON_BUTTON_ICONS.ARROW_COUNTERCLOCKWISE}
            type={ICON_BUTTON_TYPES.DEFAULT}
            hoverType={ICON_BUTTON_TYPES.BLUE}
            innerText={RELOAD_LABEL}
            onClick={onReload}
          />
        </div>
      </CanvasBox>
    );
  }
}

GraphChunkErrorBoundary.propTypes = {
  variant: PropTypes.oneOf(['mini', 'popup']),
  onReload: PropTypes.func,
  // Any value; a change clears a caught error (ShareholderGraph passes the graph object)
  resetKey: PropTypes.any,
  children: PropTypes.node
};

/**
 * @param {Object} props - forwarded to ShareholderFlowGraph (see its props)
 * @param {Object|null} props.graph - PreparedGraph; null renders the empty message without loading
 *   the chunk
 * @param {'mini'|'popup'} [props.variant]
 */
const ShareholderGraph = ({ graph = null, variant = 'mini', ...graphProps }) => {
  if (!graph) {
    return (
      <CanvasBox variant={variant}>
        <output className="sg-canvas-message">{GRAPH_COPY.EMPTY}</output>
      </CanvasBox>
    );
  }

  return (
    <GraphChunkErrorBoundary variant={variant} resetKey={graph}>
      <Suspense
        fallback={
          <CanvasBox variant={variant}>
            <GeneralLoader wrapClassName="sg-canvas-loader" />
          </CanvasBox>
        }
      >
        <LazyShareholderFlowGraph graph={graph} variant={variant} {...graphProps} />
      </Suspense>
    </GraphChunkErrorBoundary>
  );
};

ShareholderGraph.propTypes = {
  graph: PropTypes.object,
  variant: PropTypes.oneOf(['mini', 'popup']),
  companyName: PropTypes.string,
  figuresStatus: PropTypes.oneOf(['idle', 'loading', 'loaded', 'failed']),
  percentageFilter: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  getNodeHref: PropTypes.func,
  onViewNodeDetails: PropTypes.func,
  onOpenNodeInNewTab: PropTypes.func,
  onToggleFullScreen: PropTypes.func,
  focusRequest: PropTypes.shape({ nodeId: PropTypes.string }),
  onShowAllFiltered: PropTypes.func,
  onToggleNodeCollapsed: PropTypes.func,
  onOpenFilters: PropTypes.func,
  isLayoutLocked: PropTypes.bool,
  onToggleLayoutLock: PropTypes.func,
  canExport: PropTypes.bool,
  isExportDisabled: PropTypes.bool,
  onExportError: PropTypes.func,
  skipTargetId: PropTypes.string,
  highlightedNodeId: PropTypes.string,
  onConfirmDownload: PropTypes.func,
  toolbarStart: PropTypes.node
};

export default ShareholderGraph;
